import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronLeft, Info, Scale, Search, AlertCircle, Percent, CheckCircle } from 'lucide-react';
import TapToTypeValue from '@/components/TapToTypeValue';

// Complete state list with tax credit status and base tax rates (Updated 2026)
// Status: YES = full credit, NO = no credit, NA = no sales tax, PARTIAL = special rules
const STATE_OPTIONS = [
  { code: '', name: 'Select your state', status: null, taxRate: null },
  { code: 'AL', name: 'Alabama - YES', status: 'YES', taxRate: 2.0 },
  { code: 'AK', name: 'Alaska - N/A', status: 'NA', taxRate: 0 },
  { code: 'AZ', name: 'Arizona - YES', status: 'YES', taxRate: 5.6 },
  { code: 'AR', name: 'Arkansas - YES', status: 'YES', taxRate: 6.5 },
  { code: 'CA', name: 'California - NO', status: 'NO', taxRate: 7.25 },
  { code: 'CO', name: 'Colorado - YES', status: 'YES', taxRate: 2.9 },
  { code: 'CT', name: 'Connecticut - YES', status: 'YES', taxRate: 6.35 },
  { code: 'DC', name: 'Washington DC - YES', status: 'YES', taxRate: 6.0 },
  { code: 'DE', name: 'Delaware - N/A', status: 'NA', taxRate: 0 },
  { code: 'FL', name: 'Florida - YES', status: 'YES', taxRate: 6.0 },
  { code: 'GA', name: 'Georgia - YES (TAVT)', status: 'YES', taxRate: 7.0 },
  { code: 'HI', name: 'Hawaii - NO', status: 'NO', taxRate: 4.0 },
  { code: 'ID', name: 'Idaho - YES', status: 'YES', taxRate: 6.0 },
  { code: 'IL', name: 'Illinois - YES', status: 'YES', taxRate: 6.25 },
  { code: 'IN', name: 'Indiana - YES', status: 'YES', taxRate: 7.0 },
  { code: 'IA', name: 'Iowa - YES', status: 'YES', taxRate: 5.0 },
  { code: 'KS', name: 'Kansas - YES', status: 'YES', taxRate: 7.5 },
  { code: 'KY', name: 'Kentucky - YES', status: 'YES', taxRate: 6.0 },
  { code: 'LA', name: 'Louisiana - YES', status: 'YES', taxRate: 4.0 },
  { code: 'ME', name: 'Maine - YES', status: 'YES', taxRate: 5.5 },
  { code: 'MD', name: 'Maryland - YES', status: 'YES', taxRate: 6.0 },
  { code: 'MA', name: 'Massachusetts - YES', status: 'YES', taxRate: 6.25 },
  { code: 'MI', name: 'Michigan - PARTIAL', status: 'PARTIAL', taxRate: 6.0, specialRule: 'MI_CAP' },
  { code: 'MN', name: 'Minnesota - YES', status: 'YES', taxRate: 6.5 },
  { code: 'MS', name: 'Mississippi - YES', status: 'YES', taxRate: 5.0 },
  { code: 'MO', name: 'Missouri - YES', status: 'YES', taxRate: 4.225 },
  { code: 'MT', name: 'Montana - N/A', status: 'NA', taxRate: 0 },
  { code: 'NE', name: 'Nebraska - YES', status: 'YES', taxRate: 5.5 },
  { code: 'NV', name: 'Nevada - YES', status: 'YES', taxRate: 8.25 },
  { code: 'NH', name: 'New Hampshire - N/A', status: 'NA', taxRate: 0 },
  { code: 'NJ', name: 'New Jersey - YES', status: 'YES', taxRate: 6.625 },
  { code: 'NM', name: 'New Mexico - YES', status: 'YES', taxRate: 4.0 },
  { code: 'NY', name: 'New York - YES', status: 'YES', taxRate: 4.0 },
  { code: 'NC', name: 'North Carolina - YES', status: 'YES', taxRate: 3.0 },
  { code: 'ND', name: 'North Dakota - YES', status: 'YES', taxRate: 5.0 },
  { code: 'OH', name: 'Ohio - PARTIAL', status: 'PARTIAL', taxRate: 5.75, specialRule: 'OH_NEW_ONLY' },
  { code: 'OK', name: 'Oklahoma - YES', status: 'YES', taxRate: 3.25 },
  { code: 'OR', name: 'Oregon - N/A', status: 'NA', taxRate: 0 },
  { code: 'PA', name: 'Pennsylvania - YES', status: 'YES', taxRate: 6.0 },
  { code: 'RI', name: 'Rhode Island - YES', status: 'YES', taxRate: 7.0 },
  { code: 'SC', name: 'South Carolina - PARTIAL', status: 'PARTIAL', taxRate: 5.0, specialRule: 'SC_CAP' },
  { code: 'SD', name: 'South Dakota - YES', status: 'YES', taxRate: 4.0 },
  { code: 'TN', name: 'Tennessee - YES', status: 'YES', taxRate: 7.0 },
  { code: 'TX', name: 'Texas - YES', status: 'YES', taxRate: 6.25 },
  { code: 'UT', name: 'Utah - YES', status: 'YES', taxRate: 6.85 },
  { code: 'VT', name: 'Vermont - YES', status: 'YES', taxRate: 6.0 },
  { code: 'VA', name: 'Virginia - NO', status: 'NO', taxRate: 4.15 },
  { code: 'WA', name: 'Washington - YES', status: 'YES', taxRate: 7.0 },
  { code: 'WV', name: 'West Virginia - YES', status: 'YES', taxRate: 6.0 },
  { code: 'WI', name: 'Wisconsin - YES', status: 'YES', taxRate: 5.0 },
  { code: 'WY', name: 'Wyoming - YES', status: 'YES', taxRate: 4.0 },
  { code: 'Other', name: 'Other state', status: 'OTHER', taxRate: null },
];

// Get dynamic rule text based on state selection
const getStateRule = (stateCode) => {
  if (!stateCode) return null;
  
  const state = STATE_OPTIONS.find(s => s.code === stateCode);
  if (!state) return null;
  
  if (state.status === 'YES' || state.status === 'NA') {
    return {
      text: "Yes — your state gives tax savings when you trade in (tax is only on the net price after trade-in credit). This makes the dealer offer stronger on a net basis.",
      type: 'positive',
      hasTaxCredit: true
    };
  }
  
  if (state.status === 'PARTIAL') {
    // Special rules for partial credit states
    if (state.specialRule === 'OH_NEW_ONLY') {
      return {
        text: "Yes — your state gives tax savings when you trade in (tax is only on the net price after trade-in credit). This makes the dealer offer stronger on a net basis.",
        type: 'positive',
        hasTaxCredit: true,
        helperText: "Ohio's trade-in credit applies to new vehicle purchases only."
      };
    }
    if (state.specialRule === 'MI_CAP') {
      return {
        text: "Yes — your state gives tax savings when you trade in, but the credit is capped at $12,000 trade-in value (as of Jan 1, 2026).",
        type: 'positive',
        hasTaxCredit: true
      };
    }
    if (state.specialRule === 'SC_CAP') {
      return {
        text: "Yes — your state gives tax savings when you trade in, but the tax savings are capped at a maximum of $500.",
        type: 'positive',
        hasTaxCredit: true
      };
    }
  }
  
  if (state.status === 'NO') {
    return {
      text: "Your state does not give tax savings on trade-ins.",
      type: 'negative',
      hasTaxCredit: false
    };
  }
  
  if (state.status === 'OTHER') {
    return {
      text: "Most states give a tax break when you trade in. Confirm your state rule.",
      type: 'neutral',
      hasTaxCredit: null
    };
  }
  
  return null;
};

const BRAND = {
  navy: '#001F3F',
};

const TradeInBreakEven = ({ onBack, onContinue, isEmbedded = false }) => {
  const navigate = useNavigate();
  const [newCarPrice, setNewCarPrice] = useState(28000);
  const [outsideOffer, setOutsideOffer] = useState(15000);
  const [dealerOffer, setDealerOffer] = useState(12000);
  const [salesTaxRate, setSalesTaxRate] = useState(7.0);
  const [selectedState, setSelectedState] = useState('');
  const [result, setResult] = useState(null);

  // Get hasTaxCredit from selected state
  const stateRule = getStateRule(selectedState);
  const selectedStateData = STATE_OPTIONS.find(s => s.code === selectedState);
  // Use the hasTaxCredit from stateRule
  const hasTaxCredit = stateRule?.hasTaxCredit === true;
  
  // Auto-fill tax rate when state changes
  useEffect(() => {
    if (selectedStateData && selectedStateData.taxRate !== null) {
      setSalesTaxRate(selectedStateData.taxRate);
    }
  }, [selectedState, selectedStateData]);
  
  // Calculate live tax savings with special state rules
  const liveTaxSavings = React.useMemo(() => {
    if (!hasTaxCredit) return null;
    const dealer = dealerOffer || 0;
    const taxPercent = salesTaxRate || 0;
    if (dealer <= 0 || taxPercent <= 0) return null;
    
    const taxRate = taxPercent / 100;
    
    // Apply special state rules
    if (selectedStateData?.specialRule === 'MI_CAP') {
      // Michigan: credit capped at $12,000 trade-in value
      const cappedValue = Math.min(dealer, 12000);
      return cappedValue * taxRate;
    }
    if (selectedStateData?.specialRule === 'SC_CAP') {
      // South Carolina: tax savings capped at $500
      return Math.min(dealer * taxRate, 500);
    }
    // OH_NEW_ONLY uses standard calculation (assumes new vehicle)
    
    return dealer * taxRate;
  }, [dealerOffer, salesTaxRate, hasTaxCredit, selectedStateData]);

  const formatCurrency = (num) => {
    if (isNaN(num) || num === null || num === undefined) return '$0';
    return '$' + Math.round(num).toLocaleString('en-US');
  };

  const calculate = () => {
    const carPrice = newCarPrice || 0;
    const outside = outsideOffer || 0;
    const dealer = dealerOffer || 0;
    const taxPercent = salesTaxRate || 0;

    if (carPrice <= 0 || outside <= 0 || dealer <= 0) {
      return;
    }

    const taxRate = taxPercent / 100;
    
    // Private Sale Net Cost = new car price + (new car price × tax rate) – outside offer
    const privateSaleNetCost = carPrice + (carPrice * taxRate) - outside;
    
    // Dealer Net Cost calculation depends on whether state has tax credit
    let dealerNetCost;
    let taxSavings = 0;
    
    if (hasTaxCredit && taxPercent > 0) {
      // Calculate tax savings with special state rules
      if (selectedStateData?.specialRule === 'MI_CAP') {
        // Michigan: credit capped at $12,000 trade-in value
        const cappedValue = Math.min(dealer, 12000);
        taxSavings = cappedValue * taxRate;
      } else if (selectedStateData?.specialRule === 'SC_CAP') {
        // South Carolina: tax savings capped at $500
        taxSavings = Math.min(dealer * taxRate, 500);
      } else {
        // Standard calculation (including OH_NEW_ONLY which assumes new vehicle)
        taxSavings = dealer * taxRate;
      }
      
      // Dealer Net Cost = new car price + (new car price × tax rate) - tax savings - dealer offer
      dealerNetCost = carPrice + (carPrice * taxRate) - taxSavings - dealer;
    } else {
      // No tax credit - same calculation as private (full tax on car price)
      dealerNetCost = carPrice + (carPrice * taxRate) - dealer;
    }

    const difference = Math.abs(privateSaleNetCost - dealerNetCost);
    const dealerWins = dealerNetCost < privateSaleNetCost;
    const privateWins = privateSaleNetCost < dealerNetCost;

    setResult({
      carPrice,
      outside,
      dealer,
      taxSavings,
      privateSaleNetCost,
      dealerNetCost,
      difference,
      dealerWins,
      privateWins,
      hasTaxCredit: hasTaxCredit && taxPercent > 0,
    });
  };

  const reset = () => {
    setNewCarPrice(28000);
    setOutsideOffer(15000);
    setDealerOffer(12000);
    setSalesTaxRate(7.0);
    setSelectedState('');
    setResult(null);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/public-tools');
    }
  };

  const handleContinue = () => {
    if (onContinue) {
      onContinue();
    } else {
      navigate('/public-tools');
    }
  };

  const content = (
    <div className="max-w-screen-sm mx-auto px-5 py-6 space-y-5">
      
      {/* 1. Gradient Hero Banner */}
      <div 
        style={{ 
          background: 'linear-gradient(135deg, #001F3F, #1E3A8A)',
          borderRadius: '20px',
          padding: '28px 24px',
          marginBottom: '16px',
        }}
      >
        <h1 style={{ color: 'white', fontWeight: 700, fontSize: '22px', marginBottom: '8px' }}>
          Dealer Trade vs Outside Offer
        </h1>
        <p style={{ color: 'white', opacity: 0.85, fontSize: '14px', lineHeight: 1.5 }}>
          Compare your options and find out which puts more money in your pocket
        </p>
      </div>

      {/* Merged "Before You Start" card */}
      <div 
        style={{ 
          background: 'white',
          border: '1px solid #001F3F',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 12px rgba(0,31,63,0.08)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            borderRadius: '50%', 
            background: 'linear-gradient(135deg, #001F3F, #1E3A8A)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Info className="w-5 h-5" style={{ color: 'white' }} />
          </div>
          <div>
            <p style={{ fontSize: '14px', fontWeight: 700, color: '#001F3F', marginBottom: '8px' }}>
              Before You Start — Get Real Offers First
            </p>
            <p style={{ fontSize: '13px', lineHeight: 1.6, color: '#1F2937', margin: 0 }}>
              Don't rely on guesses or KBB estimates. Get real cash offers for your old car from Carvana, CarMax, Vroom, Driveo, and at least one or two local used car dealerships or private buyers. The highest offer you receive is your true benchmark. Then compare that number to the dealer's trade-in offer — after taxes are considered. This is the smartest way to know exactly how good (or bad) the dealer's number really is.
            </p>
          </div>
        </div>
      </div>

      {/* Results Section (if calculated) */}
      {result && (
        <>
          {/* 1. WINNER Card - Hero style */}
          <div 
            style={{
              background: 'linear-gradient(135deg, #001F3F, #1E3A8A)',
              borderRadius: '16px',
              padding: '24px',
              textAlign: 'center',
              marginBottom: '12px',
            }}
            data-testid="trade-in-results"
          >
            <p style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.1em', color: 'white', opacity: 0.8, marginBottom: '8px' }}>
              BETTER OFFER
            </p>
            <p style={{ fontSize: '22px', fontWeight: 700, color: 'white', marginBottom: '8px' }}>
              {result.privateWins ? 'Outside Offer' : result.dealerWins ? 'Dealer Trade' : 'Tie'}
            </p>
            <p style={{ fontSize: '32px', fontWeight: 800, color: 'white', marginBottom: '8px' }}>
              {result.privateWins || result.dealerWins ? `Wins by ${formatCurrency(result.difference)}` : 'No difference'}
            </p>
            <p style={{ fontSize: '13px', color: 'white', opacity: 0.8 }}>
              after accounting for tax savings
            </p>
          </div>

          {/* 2. Two-column stat row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            {/* Left card - Outside Offer */}
            <div 
              style={{
                background: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                padding: '14px',
                textAlign: 'center',
              }}
            >
              <p style={{ fontSize: '12px', fontWeight: 600, color: '#1F2937', marginBottom: '6px' }}>
                Outside Offer
              </p>
              <p style={{ fontSize: '22px', fontWeight: 700, color: BRAND.navy }}>
                {formatCurrency(result.outside)}
              </p>
            </div>

            {/* Right card - Dealer Trade (net) */}
            <div 
              style={{
                background: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                padding: '14px',
                textAlign: 'center',
              }}
            >
              <p style={{ fontSize: '12px', fontWeight: 600, color: '#1F2937', marginBottom: '6px' }}>
                Dealer Trade (net)
              </p>
              <p style={{ fontSize: '22px', fontWeight: 700, color: '#1F2937' }}>
                {formatCurrency(result.dealer + result.taxSavings)}
              </p>
              <p style={{ fontSize: '11px', color: '#6B7280', marginTop: '4px' }}>
                {formatCurrency(result.dealer)} + {formatCurrency(result.taxSavings)} tax savings
              </p>
            </div>
          </div>

          {/* 3. Tax savings insight card */}
          {result.hasTaxCredit && result.taxSavings > 0 && (
            <div 
              style={{
                background: 'white',
                borderTop: '3px solid #001F3F',
                borderRadius: '12px',
                padding: '16px 20px',
                boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                marginBottom: '12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                <div style={{ 
                  width: '36px', 
                  height: '36px', 
                  borderRadius: '50%', 
                  background: 'linear-gradient(135deg, #001F3F, #1E3A8A)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Percent className="w-4 h-4" style={{ color: 'white' }} />
                </div>
                <div>
                  <p style={{ fontSize: '13px', color: '#1F2937', marginBottom: '4px' }}>
                    Tax savings on trade-in
                  </p>
                  <p style={{ fontSize: '28px', fontWeight: 700, color: BRAND.navy, marginBottom: '4px' }}>
                    {formatCurrency(result.taxSavings)}
                  </p>
                  <p style={{ fontSize: '12px', color: '#6B7280' }}>
                    Your state taxes only the net price after trade-in credit
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 4. Rule for your state - compact pill badge */}
          {stateRule && result.hasTaxCredit && (
            <div style={{ marginBottom: '16px' }}>
              <div 
                style={{
                  background: '#F0F4FF',
                  borderRadius: '999px',
                  padding: '6px 14px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <CheckCircle style={{ width: '14px', height: '14px', color: '#001F3F' }} />
                <span style={{ fontSize: '13px', color: '#001F3F', fontWeight: 600 }}>
                  {selectedStateData?.name.split(' - ')[0]}: Tax credit applies — full trade-in deducted
                </span>
              </div>
            </div>
          )}

          {/* Unified Disclaimer + How do I check card */}
          <div 
            style={{ 
              background: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '16px 20px',
              boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
              marginBottom: '16px',
            }}
          >
            {/* Top section: Disclaimer (fine print) */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <div style={{ 
                width: '28px', 
                height: '28px', 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #001F3F, #1E3A8A)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Info style={{ color: 'white', width: '14px', height: '14px' }} />
              </div>
              <p style={{ fontSize: '12px', lineHeight: 1.5, color: '#1F2937', margin: 0 }}>
                Last updated April 6, 2026. Tax rules can change. Always verify with your state DMV before relying on this.
              </p>
            </div>
            
            {/* Thin divider line */}
            <div style={{ borderTop: '1px solid #E5E7EB', margin: '12px 0' }} />
            
            {/* Bottom section: How do I check (text only, no icon) */}
            <div>
              <p style={{ fontSize: '14px', fontWeight: 700, color: '#001F3F', marginBottom: '6px' }}>
                How do I check?
              </p>
              <p style={{ fontSize: '14px', lineHeight: 1.6, color: '#1F2937', margin: 0 }}>
                Search "[Your State] vehicle trade-in sales tax credit" on Google and go to the official .gov site. Confirm the rule.
              </p>
            </div>
          </div>

          {/* See how this affects my budget button */}
          <a
            href="/tools/budget"
            style={{ 
              display: 'flex',
              width: '100%',
              padding: '16px 24px',
              fontSize: '16px',
              fontWeight: 700,
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              background: 'linear-gradient(135deg, #001F3F, #1E3A8A)',
              color: '#FFFFFF',
              borderRadius: '12px',
              boxShadow: '0 4px 16px rgba(0,31,63,0.25)',
              textDecoration: 'none',
              transition: 'opacity 0.2s',
            }}
            data-testid="see-how-affects-budget-btn"
          >
            <span>See how this affects my budget</span>
            <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
          </a>

          {/* Reset Button */}
          <button
            onClick={reset}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: 600,
              backgroundColor: '#FFFFFF',
              color: '#1F2937',
              borderRadius: '50px',
              border: '2px solid #D1D5DB',
              marginTop: '12px',
            }}
          >
            Reset and try again
          </button>
        </>
      )}

      {/* Form Section (hidden when results showing) */}
      {!result && (
        <>
          {/* Inputs Card */}
          <div 
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '20px',
              boxShadow: '0 2px 12px rgba(0,31,63,0.03)',
              border: '1px solid rgba(0,31,63,0.04)',
              padding: '20px',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* 5a. New Car Price Slider */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: 600, color: '#1F2937' }}>
                    New car price
                  </label>
                  <TapToTypeValue
                    value={newCarPrice}
                    onChange={setNewCarPrice}
                    min={5000}
                    max={150000}
                    prefix="$"
                  />
                </div>
                <input
                  type="range"
                  min={5000}
                  max={150000}
                  step={500}
                  value={newCarPrice}
                  onChange={(e) => setNewCarPrice(parseInt(e.target.value))}
                  className="budget-slider"
                  style={{
                    width: '100%',
                    height: '6px',
                    cursor: 'pointer',
                    background: `linear-gradient(to right, #001F3F 0%, #001F3F ${((newCarPrice - 5000) / (150000 - 5000)) * 100}%, #E5E7EB ${((newCarPrice - 5000) / (150000 - 5000)) * 100}%, #E5E7EB 100%)`,
                  }}
                />
                <p style={{ fontSize: '12px', color: '#1F2937', marginTop: '6px' }}>
                  The price of the car you&apos;re buying (before tax)
                </p>
              </div>

              {/* 5b. Outside Offer Slider */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: 600, color: '#1F2937' }}>
                    Outside offer amount
                  </label>
                  <TapToTypeValue
                    value={outsideOffer}
                    onChange={setOutsideOffer}
                    min={0}
                    max={80000}
                    prefix="$"
                  />
                </div>
                <input
                  type="range"
                  min={0}
                  max={80000}
                  step={500}
                  value={outsideOffer}
                  onChange={(e) => setOutsideOffer(parseInt(e.target.value))}
                  className="budget-slider"
                  style={{
                    width: '100%',
                    height: '6px',
                    cursor: 'pointer',
                    background: `linear-gradient(to right, #001F3F 0%, #001F3F ${(outsideOffer / 80000) * 100}%, #E5E7EB ${(outsideOffer / 80000) * 100}%, #E5E7EB 100%)`,
                  }}
                />
                <p style={{ fontSize: '12px', color: '#1F2937', marginTop: '6px', lineHeight: 1.5 }}>
                  Your real cash offer from Carvana, CarMax, Vroom, Driveo, a private buyer, or another dealer (not the one you&apos;re buying from). KBB is just an estimate — it&apos;s not a real offer.
                </p>
              </div>

              {/* 5c. Dealer Trade Offer Slider */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: 600, color: '#1F2937' }}>
                    Dealer trade offer amount
                  </label>
                  <TapToTypeValue
                    value={dealerOffer}
                    onChange={setDealerOffer}
                    min={0}
                    max={80000}
                    prefix="$"
                  />
                </div>
                <input
                  type="range"
                  min={0}
                  max={80000}
                  step={500}
                  value={dealerOffer}
                  onChange={(e) => setDealerOffer(parseInt(e.target.value))}
                  className="budget-slider"
                  style={{
                    width: '100%',
                    height: '6px',
                    cursor: 'pointer',
                    background: `linear-gradient(to right, #001F3F 0%, #001F3F ${(dealerOffer / 80000) * 100}%, #E5E7EB ${(dealerOffer / 80000) * 100}%, #E5E7EB 100%)`,
                  }}
                />
                <p style={{ fontSize: '12px', color: '#1F2937', marginTop: '6px', lineHeight: 1.5 }}>
                  What the dealer you want to buy the new car from is offering you for your trade-in. (Tax savings only apply if you trade in and buy at the same dealership.)
                </p>
              </div>

              {/* State Selector */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#1F2937', marginBottom: '6px' }}>
                  State
                </label>
                <div style={{ position: 'relative' }}>
                  <select
                    value={selectedState}
                    onChange={(e) => {
                      setSelectedState(e.target.value);
                      setResult(null);
                    }}
                    style={{ 
                      width: '100%',
                      padding: '12px',
                      fontSize: '14px',
                      fontWeight: 500,
                      borderRadius: '12px',
                      border: '1.5px solid #E5E7EB',
                      backgroundColor: '#FAFBFC',
                      outline: 'none',
                      color: selectedState ? BRAND.navy : '#9CA3AF',
                      appearance: 'none',
                      cursor: 'pointer',
                    }}
                    data-testid="state-selector"
                  >
                    {STATE_OPTIONS.map((state) => (
                      <option key={state.code} value={state.code}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                  <svg 
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', width: '16px', height: '16px', color: '#9CA3AF' }}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* 6. Sales Tax Rate Slider */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: 600, color: '#1F2937' }}>
                    Sales tax rate (%)
                  </label>
                  <TapToTypeValue
                    value={salesTaxRate}
                    onChange={setSalesTaxRate}
                    min={0}
                    max={12}
                    suffix="%"
                    formatCommas={false}
                    step={0.1}
                  />
                </div>
                <input
                  type="range"
                  min={0}
                  max={12}
                  step={0.1}
                  value={salesTaxRate}
                  onChange={(e) => setSalesTaxRate(parseFloat(e.target.value))}
                  className="budget-slider"
                  style={{
                    width: '100%',
                    height: '6px',
                    cursor: 'pointer',
                    background: `linear-gradient(to right, #001F3F 0%, #001F3F ${(salesTaxRate / 12) * 100}%, #E5E7EB ${(salesTaxRate / 12) * 100}%, #E5E7EB 100%)`,
                  }}
                />
                {selectedStateData && selectedStateData.taxRate !== null && (
                  <p style={{ fontSize: '12px', color: '#1F2937', marginTop: '6px' }}>
                    Auto-filled with {selectedStateData.name.split(' - ')[0]} base rate. Adjust if needed for local taxes.
                  </p>
                )}
              </div>

              {/* 9. Rule for your state - navy colors instead of green/red */}
              {stateRule && (
                <div 
                  style={{ 
                    backgroundColor: 'white',
                    borderTop: '3px solid #001F3F',
                    borderRadius: '12px',
                    boxShadow: '0 2px 12px rgba(0,31,63,0.08)',
                    padding: '20px',
                  }}
                >
                  <p style={{ fontSize: '14px', fontWeight: 600, color: BRAND.navy, marginBottom: '8px' }}>
                    Rule for your state
                  </p>
                  <p 
                    style={{ 
                      fontSize: '14px',
                      lineHeight: 1.6,
                      color: stateRule.type === 'positive' ? BRAND.navy : '#1F2937'
                    }}
                  >
                    {stateRule.text}
                  </p>
                  {/* Ohio helper text */}
                  {stateRule.helperText && (
                    <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '6px', fontStyle: 'italic' }}>
                      {stateRule.helperText}
                    </p>
                  )}
                  
                  {/* Tax savings on trade-in (only for YES states) */}
                  {hasTaxCredit && liveTaxSavings !== null && (
                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #E5E7EB' }}>
                      <p style={{ fontSize: '14px', fontWeight: 600, color: BRAND.navy, marginBottom: '4px' }}>
                        Tax savings on trade-in
                      </p>
                      <p style={{ fontSize: '28px', fontWeight: 700, color: BRAND.navy }}>
                        {formatCurrency(liveTaxSavings)}
                      </p>
                      
                      {/* What that means */}
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #E5E7EB' }}>
                        <p style={{ fontSize: '14px', fontWeight: 600, color: BRAND.navy, marginBottom: '4px' }}>
                          What that means
                        </p>
                        <p style={{ fontSize: '12px', lineHeight: 1.6, color: '#1F2937' }}>
                          The dealer&apos;s offer is actually worth <strong style={{ color: BRAND.navy }}>{formatCurrency((dealerOffer || 0) + liveTaxSavings)}</strong> after taxes.<br/>
                          So the outside offer of <strong style={{ color: BRAND.navy }}>{formatCurrency(outsideOffer || 0)}</strong> is still better by <strong style={{ color: BRAND.navy }}>{formatCurrency(Math.abs((outsideOffer || 0) - ((dealerOffer || 0) + liveTaxSavings)))}</strong> in this example.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* State tax disclaimer */}
              <p style={{ fontSize: '11px', color: '#6B7280', lineHeight: 1.5, marginTop: '8px', marginBottom: '8px' }}>
                State tax rules change and local taxes/fees may apply. Use this as an estimate, then confirm with your DMV or dealer paperwork.
              </p>

              {/* Unified Disclaimer + How do I check card */}
              <div 
                style={{ 
                  background: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  padding: '16px 20px',
                  boxShadow: '0 2px 8px rgba(0,31,63,0.06)',
                }}
              >
                {/* Top section: Disclaimer (fine print) */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{ 
                    width: '28px', 
                    height: '28px', 
                    borderRadius: '50%', 
                    background: 'linear-gradient(135deg, #001F3F, #1E3A8A)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Info style={{ color: 'white', width: '14px', height: '14px' }} />
                  </div>
                  <p style={{ fontSize: '12px', lineHeight: 1.5, color: '#1F2937', margin: 0 }}>
                    Last updated April 6, 2026. Tax rules can change. Always verify with your state DMV before relying on this.
                  </p>
                </div>
                
                {/* Thin divider line */}
                <div style={{ borderTop: '1px solid #E5E7EB', margin: '12px 0' }} />
                
                {/* Bottom section: How do I check (text only, no icon) */}
                <div>
                  <p style={{ fontSize: '14px', fontWeight: 700, color: '#001F3F', marginBottom: '6px' }}>
                    How do I check?
                  </p>
                  <p style={{ fontSize: '14px', lineHeight: 1.6, color: '#1F2937', margin: 0 }}>
                    Search "[Your State] vehicle trade-in sales tax credit" on Google and go to the official .gov site. Confirm the rule.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 10. Calculate Button - gradient */}
          <button
            onClick={calculate}
            disabled={!newCarPrice || !outsideOffer || !dealerOffer || !salesTaxRate}
            style={{
              display: 'flex',
              width: '100%',
              padding: '16px',
              fontSize: '16px',
              fontWeight: 700,
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              background: 'linear-gradient(135deg, #001F3F, #1E3A8A)',
              color: '#FFFFFF',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer',
              opacity: (!newCarPrice || !outsideOffer || !dealerOffer || !salesTaxRate) ? 0.5 : 1,
            }}
            data-testid="calculate-btn"
          >
            <span>Calculate</span>
            <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </>
      )}

      {/* Continue Button (for embedded mode) */}
      {isEmbedded && (
        <button
          onClick={handleContinue}
          style={{
            display: 'flex',
            width: '100%',
            padding: '14px 20px',
            fontSize: '14px',
            fontWeight: 600,
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            backgroundColor: '#FFFFFF',
            color: BRAND.navy,
            borderRadius: '50px',
            border: `2px solid ${BRAND.navy}`,
          }}
          data-testid="continue-btn"
        >
          <span>Continue my journey</span>
          <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
        </button>
      )}

      {/* Footer Disclaimer */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', paddingTop: '8px' }}>
        <Info className="w-3.5 h-3.5" style={{ color: '#9CA3AF' }} />
        <p style={{ fontSize: '12px', fontWeight: 500, color: '#9CA3AF' }}>
          Education only. Not financial, tax, or legal advice.
        </p>
      </div>
    </div>
  );

  if (isEmbedded) {
    return content;
  }

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '128px', backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(to bottom, #FFFFFF 0%, #FAFBFC 100%)',
        borderBottom: '1px solid #E8EAED',
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
      }}>
        <div className="max-w-screen-sm mx-auto px-5 py-4">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={handleBack}
                style={{ padding: '4px', marginLeft: '-8px', color: BRAND.navy, background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <ChevronLeft className="w-6 h-6" strokeWidth={2} />
              </button>
              <h1 style={{ fontSize: '18px', fontWeight: 700, color: BRAND.navy }}>
                Trade-In Checker
              </h1>
            </div>
            <span style={{ fontSize: '14px', fontWeight: 600, color: BRAND.navy }}>
              VINdicated.<span style={{ color: '#FF4136' }}>ai</span>
            </span>
          </div>
        </div>
      </div>

      {content}
    </div>
  );
};

export default TradeInBreakEven;
